import { CircularProgress, ClickAwayListener, Grow, MenuItem, MenuList, Paper, Popper, Tooltip, Typography, IconButton, Chip, Popover, Button } from "@mui/material";
import { useMemo, useState, useRef, useEffect, useCallback, FC, Suspense, lazy } from "react";
import { useSelector } from "react-redux";
import { ErrorCode, useDropzone } from "react-dropzone";
import classNames from "classnames";
import { MessageInputProps, RootState } from "../assets/interfaces/interfaces";
import CloseIcon from "@mui/icons-material/Close";
import { DocumentAddIcon } from "./Icons/IconsDocumentAdd";
import { DocumentFilledIcon } from "./Icons/IconsDocumentFilled";
import SuggestionPrompts from "./SuggestionPrompts";
import { useAppDispatch } from "../redux/store";
import { debounce } from "lodash";
import dayjs from "dayjs";
import { getAuth } from "firebase/auth";
import useUploadImages from "../hooks/useUploadImages";
import { showNotification } from "../redux/slices/notificationSlice";
import TextBanner from "./ui/TextBanner";
import { ActivityName, updateUserActivities, updateUserActivity, UserActivitiesState } from "../redux/slices/userActivitiesSlice";
import { AttachFileRounded, DrawRounded, SendRounded } from "@mui/icons-material";
import { setShowSettings } from "../redux/slices/responseCustomizationSlice";
import { ReactComponent as StopStrokedCircle } from "../assets/icons/StopStrokedCircle.svg";
import { logClickEvent } from "../analytics";
import { AuthActions } from "./Sidebar/constants";
import { openAuthModal } from "../redux/slices/userSlice";

const MultimediaUploadPromotionModal = lazy(() => import(/* webpackPrefetch: true */ "./MultimediaUploadPromotionModal"));

const MAX_UPLOAD_FILES = 5;
const MAX_IMAGE_FILE_SIZE = 1024 * 1024 * 20;
const MAX_IMAGE_FILE_SIZE_IN_MB = "20MB";
const ACCEPTED_IMAGE_TYPE = {
  "image/jpeg": [".jpeg", ".jpg"],
  "image/webp": [],
  "image/png": [".png"],
  "image/gif": [".gif"],
};
const FILE_UPLOAD_ERROR_MAPPING = {
  [ErrorCode.FileInvalidType]: {
    title: "Invalid File Format",
    message: "The file format is not supported. Please upload files in JPG, JPEG, PNG, GIF or WEBP format only.",
  },
  [ErrorCode.FileTooLarge]: {
    title: "Invalid File Size",
    message: `The file you have selected is too large. Please ensure your file is ${MAX_IMAGE_FILE_SIZE_IN_MB} or smaller before uploading.`,
  },
  [ErrorCode.TooManyFiles]: {
    title: "Upload Limit Reached",
    message: "You’ve exceeded the maximum image upload for a single message. To upload a new file, please delete an existing upload.",
  },
};

const MessageInput: FC<MessageInputProps> = ({
  sessionId,
  handleSendMessage,
  setMessages,
  handleCancel,
  showPrompts,
  handlePromptSelect,
}) => {
  const dispatch = useAppDispatch();
  const [inputValue, setInputValue] = useState("");
  const [hoveredImage, setHoveredImage] = useState(null);
  const [attachMenuOpen, setAttachMenuOpen] = useState(false);
  const [isPromotionModalOpen, setIsPromotionModalOpen] = useState(false);
  const [loadPromotionModal, setLoadPromotionModal] = useState(false);
  const user = useSelector((state: RootState) => state.auth.currentUser);
  const { activities, isActivitiesLoaded } = useSelector((state: { userActivities: UserActivitiesState }) => state.userActivities);
  const messageTextArea = useRef<HTMLTextAreaElement>(null);
  const attachButton = useRef<HTMLButtonElement>(null);
  const fileUploadInput = useRef<HTMLInputElement>(null);
  const { images, isImageLoading, uploadImageFiles, removeImage, clearImages } = useUploadImages();

  const [isFocused, setIsFocused] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const dragCounter = useRef(0);
  const [responseCustomizationPrompt, setResponseCustomizationPrompt] = useState(false);
  const [anchorElDialog, setAnchorElDialog] = useState(null);

  const handleDrag = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
  }, []);
  const handleDragIn = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    dragCounter.current++;
    if (event.dataTransfer.items && event.dataTransfer.items.length > 0) {
      setIsDragging(true);
    }
  }, []);
  const handleDragOut = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    dragCounter.current--;
    if (dragCounter.current > 0) return;
    setIsDragging(false);
  }, []);
  const handleDrop = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);
    dragCounter.current = 0;
  }, []);

  useEffect(() => {
    window.addEventListener("dragenter", handleDragIn);
    window.addEventListener("dragleave", handleDragOut);
    window.addEventListener("dragover", handleDrag);
    window.addEventListener("drop", handleDrop);
    return function cleanUp() {
      window.removeEventListener("dragenter", handleDragIn);
      window.removeEventListener("dragleave", handleDragOut);
      window.removeEventListener("dragover", handleDrag);
      window.removeEventListener("drop", handleDrop);
      if (images.length) {
        images.forEach((image) => {
          URL.revokeObjectURL(image.url);
        });
      }
    };
  });

  const onDropAccepted = useCallback((files) => {
    uploadImageFiles(files);
  }, []);

  const onDropRejected = useCallback((files) => {
    for (const file of files) {
      const error = file.errors[0];
      dispatch(showNotification({ ...FILE_UPLOAD_ERROR_MAPPING[error.code], severity: "error", horizontal: "center" }));
      if (error.code === ErrorCode.TooManyFiles) {
        return;
      }
    }
  }, [dispatch]);

  const handleFiles = useCallback((files) => {
    if (files.length > MAX_UPLOAD_FILES - images.length) {
      const fileErrorObj = Array.from(files).map((file) => {
        return { file, errors: [{ message: FILE_UPLOAD_ERROR_MAPPING[ErrorCode.TooManyFiles], code: ErrorCode.TooManyFiles }] };
      });
      onDropRejected(fileErrorObj);
    }
    else {
      const accepted = [];
      const rejected = [];
      for (const file of files) {
        if (file.size > MAX_IMAGE_FILE_SIZE) {
          rejected.push({ file, errors: [{ message: FILE_UPLOAD_ERROR_MAPPING[ErrorCode.FileTooLarge], code: ErrorCode.FileTooLarge }] });
        }
        else if (!Object.keys(ACCEPTED_IMAGE_TYPE).some(type => file.type === type)) {
          rejected.push({ file, errors: [{ message: FILE_UPLOAD_ERROR_MAPPING[ErrorCode.FileInvalidType], code: ErrorCode.FileInvalidType }] });
        }
        else {
          accepted.push(file);
        }
      }
      if (accepted.length) {
        onDropAccepted(accepted);
      }
      if (rejected.length) {
        onDropRejected(rejected);
      }
    }
  }, [images]);

  const handleFileInput = useCallback((event) => {
    const files = event.target.files;
    handleFiles(files);
    fileUploadInput.current.value = "";
  }, [handleFiles, fileUploadInput]);

  const debouncedPaste = useCallback(debounce((event) => {
    const files = Array.from(event.clipboardData.files);
    handleFiles(files);
  }, 500, { leading: true }), [handleFiles]);

  const handlePaste = useCallback((event) => {
    const item = event.clipboardData.items[0];
    if (item.kind === "file") {
      event.preventDefault();
      debouncedPaste(event);
    }
  }, [debouncedPaste]);

  const maxFiles = useMemo(() => Math.max(MAX_UPLOAD_FILES - images.length, 0), [images]);

  const { isDragActive, getRootProps } = useDropzone({
    maxFiles,
    maxSize: MAX_IMAGE_FILE_SIZE,
    accept: ACCEPTED_IMAGE_TYPE,
    onDropAccepted,
    onDropRejected,
    validator: () => {
      if (maxFiles <= 0) {
        return { message: ErrorCode.TooManyFiles, code: ErrorCode.TooManyFiles };
      }
    },
  });

  const finchatMessage = useSelector(
    (state: RootState) => state?.finchat?.finchatMessages?.[sessionId],
  );
  const { showSettings, savedSessionPreferences, isFetching } = useSelector((state: { responseCustomization }) => state.responseCustomization);

  const auth = getAuth();
  const isUserSignedIn = useMemo(() => {
    return !!auth?.currentUser;
  }, [auth?.currentUser]);

  const finchatmessageLoading = finchatMessage?.outputLoading;
  const imagePromptActivity = activities[ActivityName.IMAGE_PROMPT_TUTORIAL];
  const customizeResponsePromptActivity = activities[ActivityName.RESPONSE_CUSTOMIZATION_TOOLTIP];
  const showBanner = isActivitiesLoaded && !imagePromptActivity.completed
    && (imagePromptActivity.prompt_after === null || dayjs().isAfter(dayjs(imagePromptActivity.prompt_after)));
  const showCustomizeResponseTooltip = !customizeResponsePromptActivity.completed && isActivitiesLoaded;

  useEffect(() => {
    if (showBanner) {
      setLoadPromotionModal(true);
    }
  }, [showBanner]);

  const completeUserActivity = () => {
    if (!imagePromptActivity.completed) {
      dispatch(updateUserActivity({
        userId: user.userId,
        activity: ActivityName.IMAGE_PROMPT_TUTORIAL,
        completed_time: new Date().toISOString(),
      }));
    }
  };

  const handleSend = () => {
    if (!finchatmessageLoading && isUserSignedIn) {
      if (images.length) {
        handleSendMessage(inputValue, "user_type", { images: images.map(image => ({ image_url: image.src, file_id: image.fileId })) });
        completeUserActivity();
      }
      else {
        handleSendMessage(inputValue, "user_type");
      }
      setInputValue("");
      clearImages();
    }
    else {
      if (!finchatMessage?.outputLoading) dispatch(openAuthModal({ type: AuthActions.SIGNIN }));
    }
  };

  const handleAutoHeight = () => {
    if (messageTextArea.current) {
      messageTextArea.current.style.height = "auto"; // Reset height to auto to calculate new height

      const computedStyle = window.getComputedStyle(messageTextArea.current);
      const maxHeightValue = computedStyle.getPropertyValue("max-height");
      const numericMaxHeight = parseFloat(maxHeightValue.replace("px", ""));

      const textareaHeight = messageTextArea.current.scrollHeight;

      messageTextArea.current.style.height = `${Math.min(
        textareaHeight,
        numericMaxHeight,
      )}px`;
      if (textareaHeight >= numericMaxHeight) {
        messageTextArea.current.style.overflowY = "auto";
      }
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault(); // Prevent default action (new line) when Enter is pressed without Shift

      if (pendingQueries[sessionId]) {
        return;
      }
      if (!finchatmessageLoading && isUserSignedIn && !isImageLoading) {
        if (images.length) {
          handleSendMessage(inputValue, "user_type", { images: images.map(image => ({ image_url: image.src, file_id: image.fileId })) });
          completeUserActivity();
        }
        else {
          handleSendMessage(inputValue, "user_type");
        }
        setInputValue("");
        clearImages();
        if (messageTextArea.current) {
          messageTextArea.current.style.height = "auto";
        }
      }
      else {
        if (!isUserSignedIn) {
          setMessages({});
          dispatch(openAuthModal({ type: AuthActions.SIGNIN }));
        }
      }
    }
  };

  const updatePromptAfter = () => {
    dispatch(updateUserActivities({ activityName: ActivityName.IMAGE_PROMPT_TUTORIAL, activity: {
      prompt_after: dayjs().add(3, "day").toDate().toISOString(),
    } }));
    dispatch(updateUserActivity({ userId: user.userId, activity: ActivityName.IMAGE_PROMPT_TUTORIAL, prompt_after_time: true }));
  };

  const onBannerActionClick = () => {
    setIsPromotionModalOpen(true);
    updatePromptAfter();
  };

  const onBannerCloseClick = () => {
    updatePromptAfter();
  };

  useEffect(() => {
    if (inputValue === "" && messageTextArea.current) {
      messageTextArea.current.style.height = "auto"; // Reset height to auto to ensure it shrinks back
    }
  }, [inputValue]);

  const { status } = useSelector((state: { plan }) => state.plan);
  const authLoading = useSelector((state: { auth }) => state.auth.loading);
  const { pendingQueries, isCancellingQuery } = useSelector((state: RootState) => state.session);
  const disableSend = (!inputValue.trim().length && !images.length) || isImageLoading || authLoading || status !== "succeeded" || finchatmessageLoading || isCancellingQuery;
  const disableAttach = authLoading || status !== "succeeded" || finchatmessageLoading;

  const handleFetchSavedPreferenes = () => {
    logClickEvent("customize_response_click");
    if (!sessionId) return;
    const existingPreferences = savedSessionPreferences[sessionId];
    if (existingPreferences) {
      dispatch(setShowSettings(true));
    }
  };

  const handleClose = (showSettings = false) => {
    setAnchorElDialog(null);
    setResponseCustomizationPrompt(false);
    dispatch(updateUserActivities({ activityName: ActivityName.RESPONSE_CUSTOMIZATION_TOOLTIP, activity: {
      completed: true,
    } }));
    dispatch(updateUserActivity({ userId: user.userId, activity: ActivityName.RESPONSE_CUSTOMIZATION_TOOLTIP, completed_time: new Date().toISOString() }));
    if (showSettings) {
      dispatch(setShowSettings(true));
    }
  };

  return (
    <div className="relative pt-2 md:pt-0 min-w-[312px] max-w-[768px] p-0 md:px-6 mx-auto z-50 md:z-auto">
      {!showSettings && showPrompts && (
        <SuggestionPrompts onPromptSelect={handlePromptSelect} />
      )}
      {
        showBanner && (
          <TextBanner
            className="mb-7"
            icon={<DocumentFilledIcon className="w-9 h-9 text-[#455A64] dark:text-tgpt-secondary-dark" altClassName="fill-white dark:fill-[#2C2C2C]" />}
            title="Free Image Uploads"
            text="Upload any image, ask questions, and receive trade insights in seconds."
            buttonText="Guide me"
            actionCallback={onBannerActionClick}
            closeCallback={onBannerCloseClick}
          />
        )
      }
      <Popover
        open={responseCustomizationPrompt}
        anchorEl={anchorElDialog}
        anchorOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        slotProps={{
          paper: {
            sx: {
              marginTop: "-16px",
              animation: "popoverSlideDown 800ms ease 1ms forwards",
              bgcolor: "#323232",
            },
          } }}
      >
        <div className="p-6 w-modal-sm bg-components-snackbar-fill text-white flex flex-col gap-y-2">
          <Typography variant="body1" className="font-semibold">TradeGPT Now Adapts to You</Typography>
          <Typography variant="body2/regular-400">Adjust how TradeGPT responds to better align with your trading preferences and needs.</Typography>
          <div className="flex justify-between w-full py-1">
            <Button variant="text" className="!text-transform-none !text-mui-white-56 hover:!bg-tgpt-secondary-states-hover" onClick={() => handleClose()}>Dismiss</Button>
            <Button variant="contained" className="!text-transform-none !bg-tgpt-secondary-main hover:!bg-tgpt-secondary-dark" onClick={() => handleClose(true)}>Try Now</Button>
          </div>
        </div>
      </Popover>
      <form className={classNames("stretch pt-1 flex flex-col border dark:border-mui-white-23 rounded-lg",
        { "shadow-md dark:shadow-xl": isFocused })}
      >
        <div className="flex w-full items-center">
          <div className="flex gap-2 px-10 md:px-6">
            {
              images.map((image, index) => (
                <div
                  onMouseEnter={() => setHoveredImage(index)}
                  onMouseLeave={() => setHoveredImage(null)}
                  key={image.url}
                  className="relative pr-2 pt-2"
                >
                  {
                    hoveredImage === index
                    && (
                      <div className="absolute top-0 right-0">
                        <IconButton
                          onClick={() => removeImage(image.url, image.fileId)}
                          className="rounded-full !bg-mui-black-56 dark:!bg-mui-white-56 !p-0.5 z-40"
                        >
                          <CloseIcon className="!w-4 !h-4 dark:text-black text-white" />
                        </IconButton>
                      </div>
                    )
                  }
                  <div className="flex relative justify-center items-center w-full h-full rounded-lg border-[0.75px] border-elevation-outline dark:border-mui-white-12 overflow-hidden">
                    {image.isLoading
                    && (
                      <>
                        <div className="absolute inset-0 bg-common-white-overlay-60 z-10" />
                        <div className="absolute inset-0 flex items-center justify-center z-20">
                          <CircularProgress
                            size={20}
                            disableShrink
                            color="inherit"
                            className="text-tgpt-secondary-main"
                          />
                        </div>
                      </>
                    )}
                    <>
                      <img
                        src={image.url}
                        alt="uploaded image"
                        className="w-12 h-12 object-cover z-0"
                      />
                    </>
                  </div>
                </div>
              ))
            }
          </div>
        </div>
        <div className="relative flex flex-col h-full mx-[12px] flex-1 items-stretch">
          <div className="overflow-hidden pt-2">
            <textarea
              ref={messageTextArea}
              value={inputValue}
              rows={2}
              onChange={(e) => {
                handleAutoHeight();
                setInputValue(e.target.value);
              }}
              onKeyDown={handleKeyPress}
              tabIndex={0}
              placeholder="Message TradeGPT"
              className="!w-full bg-transparent mx-auto ps-1 resize-none dark:text-white placeholder-mui-black-38 dark:placeholder-mui-white-38 scroll-container max-h-52 "
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
              onPaste={handlePaste}
            >
            </textarea>
          </div>
          <div className="flex items-center justify-between pt-2 pb-[12px]">
            <div className="flex gap-x-2 items-center">
              <button
                ref={attachButton}
                disabled={disableAttach}
                onClick={(event) => {
                  event.preventDefault();
                  setAttachMenuOpen(true);
                }}
                className={classNames(
                  "rounded-full relative flex items-center justify-center !bg-tgpt-secondary-accentLight disabled:opacity-10 disabled:text-gray-400",
                  {
                    "!w-[30px] !h-[30px] !px-[5px]": !isPromotionModalOpen,
                    "before:content-[''] before:absolute before:rounded-full before:animate-pulseBackground dark:before:animate-pulseBackgroundDark": showBanner,
                    "!w-[12] !h-12 !m-[-9px] px-[12.5px] !rounded-full !border-[1.5px] !border-primary-blue-focusVisible bg-mui-black-50": isPromotionModalOpen,
                    "animate-pulseShadow dark:animate-pulseShadowDark": isPromotionModalOpen,
                    "bg-[radial-gradient(circle_at_center,rgba(242,242,245,0.3)_15%,rgba(242,242,245,1)_100%)]": isPromotionModalOpen,
                    "dark:bg-[radial-gradient(circle_at_center,rgba(91,91,93,0.3)_15%,rgba(91,91,93,1)_100%)]": isPromotionModalOpen,
                  },
                )}
              >
                <div className=" z-10">
                  <Tooltip title="Attach File">
                    <AttachFileRounded className="!text-tgpt-secondary-main !w-5 !h-5" />
                  </Tooltip>
                </div>
              </button>
              <Chip
                label={(
                  <span className="flex items-center gap-x-2">
                    <Typography className="!text-[11px] !leading-none">CUSTOMIZE RESPONSE</Typography>
                    {showCustomizeResponseTooltip && (
                      <span className="inline-block h-2 w-2 rounded-full bg-primary-blue-main"></span>
                    )}
                  </span>
                )}
                onClick={handleFetchSavedPreferenes}
                onMouseEnter={(event) => {
                  if (showCustomizeResponseTooltip) {
                    setResponseCustomizationPrompt(true);
                    setAnchorElDialog(event.currentTarget);
                  }
                }}
                disabled={isFetching || disableAttach}
                className={classNames(
                  "!text-tgpt-secondary-main !p-2",
                  {
                    "!bg-tgpt-secondary-accentLight hover:!bg-action-hover": !showSettings,
                    "!bg-tgpt-secondary-accent": showSettings,
                    "before:absolute before:inset-0 before:rounded-full before:content-[''] before:z-[-1] before:animate-haloPulse": responseCustomizationPrompt,
                  },
                )}
                size="medium"
                variant="filled"
                icon={
                  <DrawRounded className="!text-tgpt-secondary-main !h-5 !w-5" />
                }
              />
            </div>
            {finchatMessage?.outputLoading || pendingQueries[sessionId]
              ? (
                  <IconButton
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      handleCancel(finchatMessage?.chat_id);
                    }}
                    aria-label="Stop generating"
                    disabled={pendingQueries[sessionId]}
                  >
                    <StopStrokedCircle className="!w-6 !h-6 text-black dark:text-white" />
                  </IconButton>
                )
              : (
                  <IconButton
                    disabled={disableSend}
                    onClick={handleSend}
                    className="text-mui-black-56 dark:!text-mui-white-56 disabled:!text-mui-black-38  disabled:dark:!text-mui-white-38"
                  >
                    <Tooltip title="Send Message">
                      <SendRounded className="!w-6!h-6" />
                    </Tooltip>
                  </IconButton>
                )}
          </div>

          <div>
            <Popper
              open={attachMenuOpen}
              anchorEl={attachButton.current}
              role={undefined}
              placement="top-start"
              transition
            >
              {({ TransitionProps, placement }) => (
                <Grow
                  {...TransitionProps}
                  style={{
                    transformOrigin:
                          placement === "bottom-start" ? "left top" : "left bottom",
                  }}
                >
                  <Paper className="dark:bg-white">
                    <ClickAwayListener onClickAway={() => setAttachMenuOpen(false)}>
                      <MenuList
                        autoFocusItem={attachMenuOpen}
                        id="composition-menu"
                        aria-labelledby="composition-button"
                      >
                        <MenuItem
                          className="gap-3"
                          onClick={() => {
                            fileUploadInput.current.click();
                            setAttachMenuOpen(false);
                            logClickEvent("multimedia_upload_click");
                          }}
                        >
                          <DocumentAddIcon />
                          Upload from computer
                        </MenuItem>
                      </MenuList>
                    </ClickAwayListener>
                  </Paper>
                </Grow>
              )}
            </Popper>
            <input
              ref={fileUploadInput}
              accept="image/gif, image/png, image/jpg, image/jpeg, image/webp"
              type="file"
              onChange={handleFileInput}
              className="hidden"
              multiple
            />
          </div>

        </div>
      </form>
      <div className="relative px-2 py-2 text-center text-xs text-token-text-secondary md:px-[60px]">
        <span>
          All TradeGPT responses are AI-generated and intended for informational purposes only, not as personalized advice. For tailored guidance, please consult a qualified financial advisor.
        </span>
      </div>
      <div {...getRootProps({ className: `absolute flex items-center justify-center top-0 left-0 w-full h-full ${isDragging ? "visible" : "invisible"} ${isDragActive ? "backdrop-blur opacity-80 dark:bg-background/paper-elevation-8 bg-white z-10" : ""}` })}>
        {
          isDragActive
          && (
            <div className="flex flex-col gap-1 items-center text-mui-black-87 dark:text-white ">
              <DocumentFilledIcon className="text-[#58585D] dark:text-mui-light-gray" altClassName="fill-white dark:fill-[#2C2C2C]" />
              <Typography variant="h6/medium-500">Add anything</Typography>
              <Typography variant="body2/regular-400">
                Drop any file here to add it to the conversation
              </Typography>
            </div>
          )
        }
      </div>
      {
        loadPromotionModal && (
          <Suspense fallback={null}>
            <MultimediaUploadPromotionModal
              open={isPromotionModalOpen}
              onClose={() => setIsPromotionModalOpen(false)}
            />
          </Suspense>
        )
      }
    </div>
  );
};

export default MessageInput;
